import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { MatchReviewDecision } from '@/types/match-review';
import type { ReportScreeningOutcome } from '@/types/screening-outcome';

export interface UseScreeningOutcomeResult {
  outcome: ReportScreeningOutcome | null;
  isLoading: boolean;
  isMutating: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /**
   * Admin triage: reopen one retained finding and record the verdict in one
   * step. The verdict sits pending until `applyVerdicts` submits it.
   */
  review: (findingId: string, decision: MatchReviewDecision, note?: string | null) => Promise<void>;
  /** Apply every recorded verdict; a rejection re-runs remediation. */
  applyVerdicts: () => Promise<void>;
}

/**
 * A report's screening outcome: the rating roll-up, each subject's rating
 * and counts, and (for admins) the retained findings that explain it —
 * plus the triage actions that let an admin fix a wrong match from here.
 * `null` reportId disables the fetch.
 */
export function useScreeningOutcome(
  reportId: string | null | undefined,
  options: { enabled?: boolean } = {}
): UseScreeningOutcomeResult {
  const enabled = options.enabled ?? true;
  const [outcome, setOutcome] = useState<ReportScreeningOutcome | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!reportId || !enabled) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      setOutcome(await vnextApiClient.getScreeningOutcome(reportId));
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load the screening outcome');
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [reportId, enabled]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const review = useCallback(
    async (findingId: string, decision: MatchReviewDecision, note?: string | null) => {
      if (!reportId) {
        return;
      }
      try {
        setIsMutating(true);
        const reopened = await vnextApiClient.reopenScreeningFinding(reportId, findingId);
        await vnextApiClient.decideMatchReview(reportId, reopened.id, decision, note);
        notifications.show({
          title: decision === 'confirm_match' ? 'Match confirmed' : 'Match rejected',
          message: 'The verdict is recorded — apply the verdicts to make it take effect.',
          color: 'green',
        });
        await refetch();
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to record the verdict');
        Sentry.captureException(e);
        notifications.show({ title: 'Verdict not recorded', message: e.message, color: 'red' });
        throw e;
      } finally {
        setIsMutating(false);
      }
    },
    [reportId, refetch]
  );

  const applyVerdicts = useCallback(async () => {
    if (!reportId) {
      return;
    }
    try {
      setIsMutating(true);
      const result = await vnextApiClient.submitMatchReview(reportId);
      notifications.show({
        title: 'Verdicts applied',
        message:
          result.rerunSubjectIds.length > 0
            ? `Remediation is re-running for ${result.rerunSubjectIds.length} subject(s); the rating and report follow.`
            : 'Nothing changed materially — the record stays as decided.',
        color: 'green',
      });
      await refetch();
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to apply the verdicts');
      Sentry.captureException(e);
      notifications.show({ title: 'Verdicts not applied', message: e.message, color: 'red' });
      throw e;
    } finally {
      setIsMutating(false);
    }
  }, [reportId, refetch]);

  return { outcome, isLoading, isMutating, error, refetch, review, applyVerdicts };
}
